import { FiArrowRight } from 'react-icons/fi';

export const connectorsTransitionsData = {
  courseId: 'connectors-transitions',
  courseType: 'vocabulary',
  title: 'Connectors & Transitions',
  icon: FiArrowRight,
  overview: {
    impact: 'Link ideas logically and smoothly — directly tested in the IELTS Coherence & Cohesion criterion',
    focus: 'Six categories of connectors (addition, contrast, cause, result, concession, sequence), punctuation rules, and formal alternatives',
    uses: 'Writing Task 2 coherence, Speaking fluency and discourse management',
    duration: '20-25 minutes'
  },

  sections: [
    {
      title: 'Categories of Connectors',
      content: `<p>Connectors (also called <strong>linking words</strong> or <strong>discourse markers</strong>) join ideas within and between sentences. They fall into six main categories.</p>
<table>
  <thead><tr><th>Category</th><th>Function</th><th>Connectors</th></tr></thead>
  <tbody>
    <tr><td>Addition</td><td>Add more information</td><td>furthermore, moreover, in addition, additionally, also</td></tr>
    <tr><td>Contrast</td><td>Show difference or opposition</td><td>however, nevertheless, on the other hand, in contrast, conversely</td></tr>
    <tr><td>Cause</td><td>Explain why</td><td>because, since, due to, owing to, as a result of</td></tr>
    <tr><td>Result</td><td>Show what happened next</td><td>therefore, consequently, as a result, thus, hence</td></tr>
    <tr><td>Concession</td><td>Acknowledge the other side</td><td>although, even though, despite, in spite of, while</td></tr>
    <tr><td>Sequence</td><td>Order ideas</td><td>firstly, secondly, finally, subsequently, in conclusion</td></tr>
  </tbody>
</table>`,
      points: [
        'Using connectors from all six categories shows range and earns higher Coherence & Cohesion marks',
        'Don\'t overuse one category — mixing "addition" and "contrast" connectors creates balanced arguments',
        'Each connector has a specific grammatical pattern — learn whether it links clauses, sentences, or noun phrases'
      ]
    },
    {
      title: 'Using Connectors in Essays',
      content: `<p>In IELTS Writing Task 2, connectors serve specific roles in different parts of your essay.</p>
<ul>
  <li><strong>Introduction:</strong> Use concession connectors to acknowledge both sides: <em>"While some argue that…, others believe…"</em></li>
  <li><strong>Body paragraphs:</strong> Start with a topic sentence, then use addition connectors for supporting points: <em>"Furthermore, research shows that…"</em></li>
  <li><strong>Counterargument:</strong> Use contrast connectors: <em>"However, critics point out that…"</em></li>
  <li><strong>Conclusion:</strong> Use result connectors to summarise: <em>"Therefore, it can be concluded that…"</em></li>
</ul>
<p><strong>Example paragraph:</strong></p>
<p><em>Technology has greatly improved education. <strong>For instance</strong>, online courses allow students to learn at their own pace. <strong>Moreover</strong>, digital tools provide instant feedback. <strong>However</strong>, excessive screen time can harm concentration. <strong>Therefore</strong>, a balanced approach is essential.</em></p>`,
      points: [
        'Use one connector per sentence at most — overloading creates awkward, mechanical writing',
        'Vary your connector placement: start of sentence ("However, …"), mid-sentence ("… ; however, …"), or clause-initial ("Although …")',
        'IELTS examiners penalise formulaic connector use (e.g., "Firstly… Secondly… Thirdly…" in every essay)'
      ]
    },
    {
      title: 'Punctuation Rules with Connectors',
      content: `<p>Incorrect punctuation with connectors is one of the most common IELTS Writing errors. The rules depend on the type of connector.</p>
<table>
  <thead><tr><th>Connector type</th><th>Punctuation rule</th><th>Correct example</th></tr></thead>
  <tbody>
    <tr><td>Conjunctive adverbs (however, therefore, moreover)</td><td>Semicolon before OR period + capital; comma after</td><td>Costs rose; <strong>however,</strong> profits remained stable. / Costs rose. <strong>However,</strong> profits remained stable.</td></tr>
    <tr><td>Coordinating conjunctions (and, but, so)</td><td>Comma before when joining independent clauses</td><td>Costs rose<strong>,</strong> but profits remained stable.</td></tr>
    <tr><td>Subordinating conjunctions (although, because, while)</td><td>No comma if subordinate clause comes second; comma if it comes first</td><td><strong>Although</strong> costs rose, profits remained stable. / Profits remained stable <strong>although</strong> costs rose.</td></tr>
  </tbody>
</table>`,
      points: [
        'WRONG: "Costs rose, however, profits remained stable." — this is a comma splice',
        'RIGHT: "Costs rose; however, profits remained stable." OR "Costs rose. However, profits remained stable."',
        'After conjunctive adverbs at the start of a sentence, always place a comma: "Therefore, …"'
      ]
    },
    {
      title: 'Formal Alternatives',
      content: `<p>IELTS Writing requires a formal register. Replace informal connectors with their formal equivalents to raise your band score.</p>
<table>
  <thead><tr><th>Informal / Overused</th><th>Formal Alternative</th><th>Example</th></tr></thead>
  <tbody>
    <tr><td>but</td><td>however, nevertheless, yet</td><td>Taxes increased. <em>However</em>, spending did not decrease.</td></tr>
    <tr><td>so</td><td>therefore, consequently, as a result</td><td>Demand fell. <em>Consequently</em>, prices dropped.</td></tr>
    <tr><td>also</td><td>furthermore, moreover, in addition</td><td><em>Furthermore</em>, the study found that…</td></tr>
    <tr><td>and</td><td>additionally, as well as, coupled with</td><td><em>Additionally</em>, more research is needed.</td></tr>
    <tr><td>because</td><td>since, owing to, due to the fact that</td><td><em>Owing to</em> rising costs, many businesses closed.</td></tr>
    <tr><td>anyway / besides</td><td>in any case, regardless, notwithstanding</td><td><em>Regardless</em>, the policy was implemented.</td></tr>
  </tbody>
</table>`,
      points: [
        'Don\'t replace every "but" with "however" — vary your choices for a natural style',
        '"Due to" and "owing to" are followed by a noun phrase, not a clause: "due to the rain" ✓ / "due to it rained" ✗',
        'In Speaking, slightly less formal connectors are acceptable — save the most formal ones for Writing'
      ]
    }
  ],

  exercises: [
    {
      title: 'Categorise the Connector',
      type: 'select',
      instructions: 'Identify which category each connector belongs to.',
      questions: [
        {
          text: 'What category does "nevertheless" belong to?',
          options: ['Addition', 'Contrast', 'Cause', 'Result'],
          answer: 'Contrast',
          explanation: '"Nevertheless" means "in spite of that" — it introduces a contrasting or unexpected point.'
        },
        {
          text: 'What category does "consequently" belong to?',
          options: ['Contrast', 'Concession', 'Result', 'Sequence'],
          answer: 'Result',
          explanation: '"Consequently" means "as a result" — it introduces the outcome or effect of something.'
        },
        {
          text: 'What category does "furthermore" belong to?',
          options: ['Addition', 'Contrast', 'Cause', 'Concession'],
          answer: 'Addition',
          explanation: '"Furthermore" means "in addition" — it adds more information to support the same point.'
        },
        {
          text: 'What category does "despite" belong to?',
          options: ['Addition', 'Result', 'Sequence', 'Concession'],
          answer: 'Concession',
          explanation: '"Despite" acknowledges a factor that might suggest a different outcome — a concession connector.'
        }
      ]
    },
    {
      title: 'Complete with the Correct Connector',
      type: 'fill-in',
      instructions: 'Fill in the blank with the most appropriate connector from the category indicated.',
      questions: [
        {
          text: 'The economy has improved. _______, unemployment remains high. (contrast)',
          answer: 'However',
          explanation: '"However" is a contrast connector that introduces an unexpected or opposing fact.'
        },
        {
          text: 'The population is ageing rapidly. _______, healthcare costs are expected to rise. (result)',
          answer: 'Consequently',
          explanation: '"Consequently" shows the result: because the population is ageing, healthcare costs will rise.'
        },
        {
          text: '_______ the benefits of tourism, it can also damage local environments. (concession)',
          answer: 'Despite',
          explanation: '"Despite" introduces a concession — acknowledging the benefits before presenting the downside. Note: "Despite" is followed by a noun phrase.'
        },
        {
          text: 'Exercise improves physical health. _______, it has been shown to reduce stress and anxiety. (addition)',
          answer: 'Moreover',
          explanation: '"Moreover" adds a further point that strengthens the argument about the benefits of exercise.'
        }
      ]
    },
    {
      title: 'Fix the Punctuation',
      type: 'select',
      instructions: 'Choose the sentence with correct punctuation.',
      questions: [
        {
          text: 'Which sentence is correctly punctuated?',
          options: [
            'Sales increased, however, profits fell.',
            'Sales increased; however, profits fell.',
            'Sales increased however profits fell.',
            'Sales increased however, profits fell.'
          ],
          answer: 'Sales increased; however, profits fell.',
          explanation: '"However" is a conjunctive adverb. It needs a semicolon (or period) before it and a comma after it.'
        },
        {
          text: 'Which sentence is correctly punctuated?',
          options: [
            'Although it was expensive. The quality was excellent.',
            'Although it was expensive, the quality was excellent.',
            'Although, it was expensive the quality was excellent.',
            'Although it was expensive the quality was excellent.'
          ],
          answer: 'Although it was expensive, the quality was excellent.',
          explanation: 'When a subordinate clause (starting with "although") comes first, place a comma before the main clause.'
        },
        {
          text: 'Which sentence is correctly punctuated?',
          options: [
            'The results were surprising, therefore further research is needed.',
            'The results were surprising. Therefore further research is needed.',
            'The results were surprising. Therefore, further research is needed.',
            'The results were surprising therefore, further research is needed.'
          ],
          answer: 'The results were surprising. Therefore, further research is needed.',
          explanation: '"Therefore" is a conjunctive adverb. Use a period (or semicolon) before it and a comma after it.'
        },
        {
          text: 'Which sentence is correctly punctuated?',
          options: [
            'The plan failed because, the budget was cut.',
            'The plan failed, because the budget was cut.',
            'The plan failed because the budget was cut.',
            'The plan failed. Because the budget was cut.'
          ],
          answer: 'The plan failed because the budget was cut.',
          explanation: 'When "because" introduces a clause after the main clause, no comma is needed.'
        }
      ]
    },
    {
      title: 'Choose the Formal Alternative',
      type: 'select',
      instructions: 'Replace the informal connector with the most appropriate formal alternative.',
      questions: [
        {
          text: 'Formal alternative for "but" in: "Many people want change, but the government resists."',
          options: ['so', 'however', 'also', 'because'],
          answer: 'however',
          explanation: '"However" is the formal equivalent of "but" for introducing contrast between two clauses.'
        },
        {
          text: 'Formal alternative for "so" in: "Prices rose, so demand fell."',
          options: ['furthermore', 'although', 'consequently', 'moreover'],
          answer: 'consequently',
          explanation: '"Consequently" is the formal equivalent of "so" when showing a result or effect.'
        },
        {
          text: 'Formal alternative for "also" in: "Also, the study revealed new data."',
          options: ['Furthermore', 'However', 'Therefore', 'Although'],
          answer: 'Furthermore',
          explanation: '"Furthermore" is a more formal way to add information, replacing the informal "also" at the start of a sentence.'
        }
      ]
    },
    {
      title: 'Write Sentences with Connectors',
      type: 'writing',
      instructions: 'Write IELTS-style sentences using the specified connector. Pay attention to punctuation.',
      questions: [
        {
          text: 'Write a sentence using "nevertheless" to show contrast.',
          example: 'The initial results were disappointing; nevertheless, the research team continued their investigation and eventually achieved a breakthrough.',
          tip: '"Nevertheless" means "in spite of that". Place a semicolon or period before it and a comma after it.'
        },
        {
          text: 'Write a sentence using "owing to" to show cause.',
          example: 'Owing to the rapid growth in urban populations, many cities are struggling to provide adequate housing and infrastructure.',
          tip: '"Owing to" is followed by a noun phrase (not a clause). It is more formal than "because of".'
        }
      ]
    }
  ],

  tips: [
    'Learn connectors in categories (addition, contrast, cause, result, concession, sequence) rather than randomly',
    'Master punctuation rules — incorrect punctuation with connectors is a very common IELTS error',
    'Don\'t overuse connectors: one per sentence is enough; two maximum in complex sentences',
    'In Speaking, simpler connectors are fine — save "moreover" and "consequently" for Writing'
  ],
  dailyPractice: [
    'Read one IELTS model essay and highlight all connectors — identify their category',
    'Write a 4-sentence paragraph using one connector from each of four different categories',
    'Practice punctuation: write 5 sentences with conjunctive adverbs and check your commas/semicolons',
    'Take an informal paragraph and rewrite it replacing informal linkers with formal alternatives'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Connectors & Transitions: Six categories of logical connectors (addition, contrast, cause, result, concession, sequence), transition phrases for essays, punctuation rules with conjunctive adverbs vs conjunctions, and formal alternatives for informal linkers. Difficulty 3 (intermediate). Exercises include categorisation, gap-fill, punctuation correction, formality upgrading, and sentence writing.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...connectorsTransitionsData,
      title: connectorsTransitionsData.title + ' (Foundations)',
      overview: { ...connectorsTransitionsData.overview, duration: '15-20 minutes' },
      sections: connectorsTransitionsData.sections.slice(0, Math.max(2, Math.ceil(connectorsTransitionsData.sections.length / 2))),
      exercises: connectorsTransitionsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...connectorsTransitionsData,
      title: connectorsTransitionsData.title + ' (Advanced)',
      overview: { ...connectorsTransitionsData.overview, duration: '30-35 minutes' },
      sections: connectorsTransitionsData.sections,
      exercises: connectorsTransitionsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(connectorsTransitionsData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return connectorsTransitionsData;
}
